// Node Modules
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

// 3rd Party Components
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

// BinaryForge Components
import { ConnectivityPill } from "./../device/DeviceStatusPills";
import { PillItem } from "../helpers";

// Helpers
import { filterApplyTemplate } from "../../config/datatable";
import { nav } from "../../config/navigation";

// Atoms
import { selectedUserSchemeAtom } from "../../atoms/user";

const buildDefaultFilters = () => ({
	global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	name: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	id: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	schemeLabel: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	hardwareVersion: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	softwareVersion: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	batteryPercentage: {
		operator: FilterOperator.AND,
		constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
	},
	_online: { value: null, matchMode: FilterMatchMode.EQUALS },
	_inAlarm: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const AdminDevicesTable = ({ devices, loading }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const setSelectedScheme = useSetRecoilState(selectedUserSchemeAtom);
	const [filters, setFilters] = useState(() => buildDefaultFilters());
	const [globalFilterValue, setGlobalFilterValue] = useState("");

	const clearFilters = () => {
		setFilters(buildDefaultFilters());
		setGlobalFilterValue("");
	};

	const onGlobalFilterChange = (event) => {
		const { value } = event.target;
		setFilters((previousFilters) => ({
			...previousFilters,
			global: {
				...previousFilters.global,
				value,
			},
		}));
		setGlobalFilterValue(value);
	};

	const tableHeader = useMemo(
		() => (
			<div className="flex jContent-between aItems-center gap-small">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText
						value={globalFilterValue}
						onChange={onGlobalFilterChange}
						placeholder={t("admin.device.list.table.globalFilter")}
					/>
				</span>
				<Button
					type="button"
					label={t("common.clear")}
					className="p-button-text"
					icon="pi pi-filter-slash"
					onClick={clearFilters}
				/>
			</div>
		),
		[globalFilterValue, clearFilters, onGlobalFilterChange, t],
	);

	const renderValue = (value) => {
		if (value === null || value === undefined || value === "") {
			return t("admin.device.list.table.missing");
		}
		return value;
	};

	const batteryTemplate = (rowData) => {
		if (!rowData.batteryPercentage && rowData.batteryPercentage !== 0) {
			return t("admin.device.list.table.missing");
		}
		return `${rowData.batteryPercentage}%`;
	};

	const connectivityTemplate = (rowData) => <ConnectivityPill device={rowData} heuristicOnly />;

	const alarmTemplate = (rowData) =>
		rowData._inAlarm ? (
			<PillItem type="text" value={t("alarm.group.details.state.alarm")} wrapperStyle="error" />
		) : (
			<PillItem type="text" value={t("alarm.group.details.state.clear")} wrapperStyle="success" />
		);

	const onlineFilterTemplate = (options) => (
		<Dropdown
			showClear
			value={options.value}
			options={[
				{ value: true, label: t("device.state.presence.online") },
				{ value: false, label: t("device.state.presence.offline") },
			]}
			onChange={(e) => options.filterCallback(e.value)}
			placeholder={t("common.form.multiselectPlaceholder")}
		/>
	);

	const alarmFilterTemplate = (options) => (
		<Dropdown
			showClear
			value={options.value}
			options={[
				{ value: true, label: t("alarm.group.details.state.alarm") },
				{ value: false, label: t("alarm.group.details.state.clear") },
			]}
			onChange={(e) => options.filterCallback(e.value)}
			placeholder={t("common.form.multiselectPlaceholder")}
		/>
	);

	const handleViewDevice = (device) => {
		if (!device?.id || !device?.schemeId) {
			return;
		}

		setSelectedScheme({
			id: device.schemeId,
			name: device.schemeName || device.schemeId,
			role: "ADMINISTRATOR",
		});
		navigate(`${nav.device.base}/${device.id}`);
	};

	const actionsTemplate = (rowData) => (
		<Button
				type="button"
				className="p-button-text"
				onClick={() => handleViewDevice(rowData)}
				icon="pi pi-external-link"
				aria-label={t("admin.device.list.table.actions.view")}
		/>
	);

	return (
		<DataTable
			value={devices}
			loading={loading}
			stripedRows
			paginator
			rows={25}
			rowsPerPageOptions={[25, 50, 100]}
			filters={filters}
			onFilter={(event) => setFilters(event.filters)}
			filterDisplay="menu"
			header={tableHeader}
			globalFilterFields={["name", "id", "schemeLabel", "hardwareVersion", "softwareVersion", "batteryPercentage"]}
			emptyMessage={t("admin.device.list.table.empty")}
			className="adminDevicesTable"
		>
			<Column
				field="name"
				header={t("admin.device.list.table.name")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
			/>
			<Column
				field="id"
				header={t("admin.device.list.table.id")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
			/>
			<Column
				field="schemeLabel"
				header={t("admin.device.list.table.scheme")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
			/>
			<Column
				field="hardwareVersion"
				header={t("admin.device.list.table.hardware")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
				body={(rowData) => renderValue(rowData.hardwareVersion)}
			/>
			<Column
				field="softwareVersion"
				header={t("admin.device.list.table.software")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
				body={(rowData) => renderValue(rowData.softwareVersion)}
			/>
			<Column
				field="batteryPercentage"
				header={t("admin.device.list.table.battery")}
				sortable
				filter
				filterPlaceholder={t("admin.device.list.table.filterPlaceholder")}
				filterApply={filterApplyTemplate}
				body={batteryTemplate}
			/>
			<Column
				field="_online"
				header={t("devices.table.connectivityEstimated")}
				sortable
				filter
				showFilterMatchModes={false}
				filterElement={onlineFilterTemplate}
				filterApply={filterApplyTemplate}
				body={connectivityTemplate}
			/>
			<Column
				field="_inAlarm"
				header={t("devices.table.alarmState")}
				sortable
				filter
				showFilterMatchModes={false}
				filterElement={alarmFilterTemplate}
				filterApply={filterApplyTemplate}
				body={alarmTemplate}
			/>
			<Column
				header={t("admin.device.list.table.actions.header")}
				exportable={false}
				sortable={false}
				filter={false}
				body={actionsTemplate}
				bodyClassName="text-right"
			/>
		</DataTable>
	);
};

export default AdminDevicesTable;
